import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import ProfileSetup from "./pages/ProfileSetup";
import UpdatePasswordBridge from "./pages/UpdatePasswordBridge";
import Settings from "./pages/Settings";
import MapView from "./pages/MapView";
import GiftFlow from "./pages/GiftFlow";
import ThankYou from "./pages/ThankYou";
import Dashboard from "./pages/Dashboard";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Safety from "./pages/Safety";
import NotFound from "./pages/NotFound";
import SavedUsers from "./pages/SavedUsers";
import Help from "./pages/Help";
import Support from "./pages/Support";
import ResetPassword from "./pages/ResetPassword";
import UpdatePassword from "./pages/UpdatePassword";
import StripeCallback from "./pages/StripeCallback";
import Ambassador from "./pages/Ambassador";
import AdminAmbassadors from "./pages/AdminAmbassadors";
import PrizeDraw from "./pages/PrizeDraw";
import PrizeDrawConfirmation from "./pages/PrizeDrawConfirmation";
import { isMobileApp } from "@/lib/platform";
import ProtectedRoute from "@/components/ProtectedRoute";
import useAuth from "@/hooks/useAuth";
import useAutoCreateUser from "@/hooks/useAutoCreateUser";
import { MobileOnlyScreen } from "@/components/MobileOnlyScreen";
import { LocationSharingProvider } from "@/contexts/LocationSharingContext";
import { InactivityProvider } from "@/contexts/InactivityContext";
import usePushNotifications from "@/hooks/usePushNotifications";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import Spinner from "@/components/Spinner";

const queryClient = new QueryClient();

const AppContent = () => {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Auto-create user row after authentication
  useAutoCreateUser(user?.id);

  useEffect(() => {
    const checkMobile = isMobileApp();
    setIsMobile(checkMobile);
  }, []);

  // Listen for deep links on native platforms (e.g. password reset via custom scheme)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = CapApp.addListener('appUrlOpen', async (event) => {
      console.log('appUrlOpen:', event.url);
      try {
        const url = new URL(event.url);
        if (url.host === 'update-password' || url.pathname === '/update-password') {
          const accessToken = url.searchParams.get('access_token');
          const refreshToken = url.searchParams.get('refresh_token');

          if (accessToken && refreshToken) {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (error) {
              console.error('Deep link setSession error:', error);
            }
            navigate('/update-password');
          }
        }
      } catch (e) {
        console.error('appUrlOpen handler error:', e);
      }
    });

    return () => { listener.then(l => l.remove()); };
  }, [navigate]);

  // Always call hooks in the same order; guard inside hook handles web
  const platform = Capacitor.getPlatform() as 'android' | 'ios' | 'web';
  usePushNotifications({ platform });

  if (isMobile === null) {
    return <Spinner size={32} />;
  }

  // Web users: Show download screen, legal pages, Stripe callback, and ambassador pages
  if (!isMobile) {
    return (
      <Routes>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/support" element={<Support />} />
        <Route path="/stripe-callback" element={<StripeCallback />} />
        <Route path="/update-password" element={<UpdatePasswordBridge />} />
        <Route path="/ambassador" element={<Ambassador />} />
        <Route path="/admin/ambassadors" element={<AdminAmbassadors />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="*" element={<MobileOnlyScreen />} />
      </Routes>
    );
  }

  // Mobile app users: Full app access
  return (
    <InactivityProvider>
      <LocationSharingProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/profile-setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/map" element={<ProtectedRoute><MapView /></ProtectedRoute>} />
          <Route path="/gift" element={<ProtectedRoute><GiftFlow /></ProtectedRoute>} />
          <Route path="/prize-draw" element={<ProtectedRoute><PrizeDraw /></ProtectedRoute>} />
          <Route path="/prize-draw-confirmation" element={<ProtectedRoute><PrizeDrawConfirmation /></ProtectedRoute>} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/saved-users" element={<ProtectedRoute><SavedUsers /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/safety" element={<Safety />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </LocationSharingProvider>
    </InactivityProvider>
  );
};


const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <SonnerToaster />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
